import { supabase } from '../lib/supabase';
import type {
  UserRole,
  RolePermissions,
  RoleAssignment,
} from '../types/rbac';
import { DEFAULT_ROLE_PERMISSIONS } from '../types/rbac';

export const ADMIN_EMAIL = 'active.legendss@gmail.com';

const STORAGE_KEY = 'activelegend_rbac_assignments_v2';
const EVENT_NAME = 'activelegend_rbac_change';

// Default initial assignments including official studio accounts
const DEFAULT_ASSIGNMENTS: RoleAssignment[] = [
  {
    email: ADMIN_EMAIL,
    role: 'admin',
    custom_permissions: DEFAULT_ROLE_PERMISSIONS.admin,
    assigned_by: 'system',
    created_at: '2026-01-01T00:00:00.000Z',
    updated_at: '2026-01-01T00:00:00.000Z',
  },
  {
    email: 'studio@activelegend.ir',
    role: 'admin',
    custom_permissions: DEFAULT_ROLE_PERMISSIONS.admin,
    assigned_by: 'system',
    created_at: '2026-01-01T00:00:00.000Z',
    updated_at: '2026-01-01T00:00:00.000Z',
  },
  {
    email: 'ai.assistant@activelegend.ir',
    role: 'editor',
    custom_permissions: DEFAULT_ROLE_PERMISSIONS.editor,
    assigned_by: 'system',
    created_at: '2026-01-01T00:00:00.000Z',
    updated_at: '2026-01-01T00:00:00.000Z',
  },
];

class RBACService {
  private assignments: Map<string, RoleAssignment> = new Map();
  private initialized = false;

  constructor() {
    this.loadFromStorage();
  }

  private loadFromStorage() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as RoleAssignment[];
        this.assignments.clear();
        for (const item of parsed) {
          this.assignments.set(item.email.toLowerCase(), item);
        }
      } else {
        for (const item of DEFAULT_ASSIGNMENTS) {
          this.assignments.set(item.email.toLowerCase(), item);
        }
        this.saveToStorage();
      }
    } catch {
      for (const item of DEFAULT_ASSIGNMENTS) {
        this.assignments.set(item.email.toLowerCase(), item);
      }
    }

    // Always ensure ADMIN_EMAIL is admin
    this.assignments.set(ADMIN_EMAIL.toLowerCase(), {
      email: ADMIN_EMAIL,
      role: 'admin',
      custom_permissions: DEFAULT_ROLE_PERMISSIONS.admin,
      assigned_by: 'system',
      created_at: '2026-01-01T00:00:00.000Z',
    });
  }

  private saveToStorage() {
    try {
      const arr = Array.from(this.assignments.values());
      localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(EVENT_NAME));
      }
    } catch (e) {
      console.warn('Could not save RBAC state to localStorage', e);
    }
  }

  /**
   * Initializes role state from Supabase if available, while ensuring resilient fallback
   */
  public async init(): Promise<void> {
    if (this.initialized) return;
    this.initialized = true;

    try {
      // 1. Fetch any users with special status or linked roles
      const { data: users, error } = await supabase
        .from('users')
        .select('id, email, is_special')
        .limit(200);

      if (!error && users) {
        for (const u of users) {
          if (!u.email) continue;
          const emailNorm = u.email.toLowerCase();
          // If marked is_special in Supabase but not yet registered in assignments, give editor role
          if (u.is_special && !this.assignments.has(emailNorm)) {
            this.assignments.set(emailNorm, {
              email: u.email,
              role: 'editor',
              custom_permissions: DEFAULT_ROLE_PERMISSIONS.editor,
              assigned_by: 'supabase_profile',
              created_at: new Date().toISOString(),
            });
          }
        }
        this.saveToStorage();
      }
    } catch (err) {
      console.warn('RBAC Supabase sync fallback active:', err);
    }
  }

  /**
   * Get role for a given email
   */
  public getRole(email: string | null | undefined): UserRole {
    if (!email) return 'user';
    const norm = email.trim().toLowerCase();
    if (norm === ADMIN_EMAIL.toLowerCase()) return 'admin';
    const record = this.assignments.get(norm);
    return record?.role || 'user';
  }

  /**
   * Get granular permissions for a given email
   */
  public getPermissions(email: string | null | undefined): RolePermissions {
    if (!email) return { ...DEFAULT_ROLE_PERMISSIONS.user };
    const norm = email.trim().toLowerCase();
    if (norm === ADMIN_EMAIL.toLowerCase()) {
      return { ...DEFAULT_ROLE_PERMISSIONS.admin };
    }

    const record = this.assignments.get(norm);
    if (!record) return { ...DEFAULT_ROLE_PERMISSIONS.user };

    const base = DEFAULT_ROLE_PERMISSIONS[record.role] || DEFAULT_ROLE_PERMISSIONS.user;
    return {
      ...base,
      ...(record.custom_permissions || {}),
    };
  }

  /**
   * Check if an email has a specific permission
   */
  public hasPermission(email: string | null | undefined, permission: keyof RolePermissions): boolean {
    if (!email) return false;
    const perms = this.getPermissions(email);
    return Boolean(perms[permission]);
  }

  /**
   * Check if an email has at least one of the required roles
   */
  public hasRole(email: string | null | undefined, roles: UserRole | UserRole[]): boolean {
    if (!email) return false;
    const userRole = this.getRole(email);
    if (userRole === 'admin') return true; // Admins satisfy all role checks
    const targetRoles = Array.isArray(roles) ? roles : [roles];
    return targetRoles.includes(userRole);
  }

  /**
   * Returns all assigned roles
   */
  public async getAllAssignments(): Promise<RoleAssignment[]> {
    await this.init();
    return Array.from(this.assignments.values());
  }

  /**
   * Assign or update a role for an email
   */
  public async assignRole(
    email: string,
    role: UserRole,
    customPermissions?: Partial<RolePermissions>,
    assignedBy = 'admin'
  ): Promise<RoleAssignment> {
    const norm = email.trim().toLowerCase();
    if (!norm || !norm.includes('@')) {
      throw new Error('ایمیل وارد شده نامعتبر است.');
    }

    const existing = this.assignments.get(norm);
    const now = new Date().toISOString();

    const newAssignment: RoleAssignment = {
      id: existing?.id || crypto.randomUUID(),
      email: norm,
      role,
      custom_permissions: customPermissions || DEFAULT_ROLE_PERMISSIONS[role],
      assigned_by: assignedBy,
      created_at: existing?.created_at || now,
      updated_at: now,
    };

    this.assignments.set(norm, newAssignment);
    this.saveToStorage();

    // Optionally update user in Supabase if exists
    try {
      await supabase
        .from('users')
        .update({
          is_special: role === 'admin' || role === 'editor',
          updated_at: now,
        })
        .ilike('email', norm);
    } catch (e) {
      console.warn('Could not update users table for RBAC assignment:', e);
    }

    return newAssignment;
  }

  /**
   * Revoke role for an email (resets to regular 'user')
   */
  public async revokeRole(email: string): Promise<boolean> {
    const norm = email.trim().toLowerCase();
    if (norm === ADMIN_EMAIL.toLowerCase()) {
      throw new Error('امکان حذف نقش مدیر ارشد سیستم وجود ندارد.');
    }

    if (!this.assignments.has(norm)) {
      return false;
    }

    this.assignments.delete(norm);
    this.saveToStorage();

    // Reset special flag in Supabase if user exists
    try {
      await supabase
        .from('users')
        .update({
          is_special: false,
          updated_at: new Date().toISOString(),
        })
        .ilike('email', norm);
    } catch (e) {
      console.warn('Could not reset user is_special flag:', e);
    }

    return true;
  }

  /**
   * Fetch all registered users from Supabase to facilitate 1-click role assignment
   */
  public async getRegisteredUsers(): Promise<{ id: string; email: string; display_name: string | null }[]> {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, email, display_name')
        .order('created_at', { ascending: false })
        .limit(200);

      if (error) throw error;
      return (data || []).filter((u): u is { id: string; email: string; display_name: string | null } => Boolean(u.email));
    } catch {
      return [];
    }
  }

  /**
   * Subscribe to live RBAC changes
   */
  public subscribe(callback: () => void): () => void {
    if (typeof window === 'undefined') return () => {};
    window.addEventListener(EVENT_NAME, callback);
    return () => window.removeEventListener(EVENT_NAME, callback);
  }
}

export const rbacService = new RBACService();
